import React, { useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import {
  LuSearch,
  LuChevronUp,
  LuChevronDown,
  LuChevronsUpDown,
  LuChevronLeft,
  LuChevronRight,
  LuX,
  LuCalendar,
} from "react-icons/lu";
import { getRecentQuestions, getDistinctUsers, resolveDisplayNames } from "../../utils/services/api";

const ROWS_PER_PAGE_OPTIONS = [5, 10, 25, 50];

// How many numbered page buttons to show around the current page before
// collapsing the rest into "…" — mirrors the reference table's "1 2 … 951".
const PAGE_WINDOW = 1;

// Same muted tone the rest of this page already uses (ActivityLog.jsx's
// MUTED_TEXT/AXIS_TICK, e.g. the "Usage by user" chart's axis labels) —
// applied uniformly here (headers AND body, every column, same darkness)
// instead of the Tailwind gray-scale this file used before, which didn't
// match the rest of the page.
const UNIFORM_TEXT = "text-[#898781]";
const TITLE_TEXT = "text-gray-900";
const HEADER_INACTIVE = UNIFORM_TEXT;
const HEADER_ACTIVE = UNIFORM_TEXT;
// Generic (non-column-specific) secondary text -- loading/empty-state rows.
const BODY_SECONDARY = UNIFORM_TEXT;
const MUTED_TEXT = UNIFORM_TEXT;
const TOOLBAR_TEXT = "text-gray-700";
const BORDER = "border-gray-200";
const ROW_BORDER = "border-gray-100";

const formatTimestamp = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const today = new Date();
  const isToday = d.toDateString() === today.toDateString();
  const time = d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  return isToday ? `Today, ${time}` : `${d.toLocaleDateString()}, ${time}`;
};

// Builds [1, "…", 4, 5, 6, "…", 951] style page lists instead of rendering
// every page button, which would be unusable once total pages runs into the
// hundreds/thousands (real data volumes per the reference table).
const buildPageList = (current, totalPages) => {
  if (totalPages <= 1) return [1];
  const pages = new Set([1, totalPages, current]);
  for (let d = 1; d <= PAGE_WINDOW; d++) {
    if (current - d >= 1) pages.add(current - d);
    if (current + d <= totalPages) pages.add(current + d);
  }
  const sorted = Array.from(pages).sort((a, b) => a - b);
  const withGaps = [];
  sorted.forEach((p, i) => {
    if (i > 0 && p - sorted[i - 1] > 1) withGaps.push("…");
    withGaps.push(p);
  });
  return withGaps;
};

const SortHeader = ({ label, field, sortBy, sortDir, onSort, align = "left" }) => {
  const active = sortBy === field;
  const Icon = active ? (sortDir === "asc" ? LuChevronUp : LuChevronDown) : LuChevronsUpDown;
  const justifyClass = align === "right" ? "justify-end w-full" : align === "center" ? "justify-center w-full" : "justify-start";
  return (
    <button
      type="button"
      onClick={() => onSort(field)}
      className={`flex items-center ${justifyClass} gap-1 text-[11px] font-medium uppercase tracking-wider ${
        active ? HEADER_ACTIVE : HEADER_INACTIVE
      } hover:text-gray-600`}
    >
      {label}
      <Icon size={13} />
    </button>
  );
};

const RecentQueryLogTable = () => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [sortBy, setSortBy] = useState("time");
  const [sortDir, setSortDir] = useState("desc");

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState(""); // debounced value actually sent to the server

  const [selectedUser, setSelectedUser] = useState(""); // "" = All Users
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [userSearchTerm, setUserSearchTerm] = useState("");
  const [allUsers, setAllUsers] = useState([]); // [{user_id, label}]
  const userMenuRef = useRef(null);

  const [pageJumpInput, setPageJumpInput] = useState("");

  // Applied date range (native <input type="date"> "YYYY-MM-DD" strings, or
  // "" for no bound) — draftFrom/draftTo hold in-progress edits inside the
  // popover until "Apply" is clicked, so typing doesn't refetch on every
  // keystroke.
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [draftFrom, setDraftFrom] = useState("");
  const [draftTo, setDraftTo] = useState("");
  const [dateMenuOpen, setDateMenuOpen] = useState(false);
  const dateMenuRef = useRef(null);

  const [userNames, setUserNames] = useState({});

  const labelForUser = (userId) => {
    if (!userId || userId === "unknown") return "Unknown user (legacy)";
    return userNames[userId] || userId;
  };

  // Debounce the free-text search so every keystroke doesn't fire a request.
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Close the "All Users"/date-range dropdowns on outside click.
  useEffect(() => {
    const onClick = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
      if (dateMenuRef.current && !dateMenuRef.current.contains(e.target)) {
        setDateMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  // Distinct users for the "All Users" filter — a dedicated, cheap endpoint
  // reading the same collection the table itself uses (see
  // CosmosLogger.get_distinct_users), not the much larger/slower unfiltered
  // usage-summary aggregation.
  useEffect(() => {
    getDistinctUsers()
      .then((ids) => {
        setAllUsers(ids.map((id) => ({ user_id: id })));
        if (ids.length > 0) {
          resolveDisplayNames(ids).then((names) => setUserNames((prev) => ({ ...prev, ...names })));
        }
      })
      .catch(() => {
        toast.error("Failed to load the user list — the \"All Users\" filter may be incomplete.");
      });
  }, []);

  const totalPages = Math.max(1, Math.ceil(total / rowsPerPage));

  // "YYYY-MM-DD" (local, from the native date input) -> full UTC ISO bound.
  // dateFrom is the start of that local day, dateTo the end of it, so the
  // whole selected day range is included regardless of the viewer's timezone
  // offset from the UTC timestamps chat_history.time is stored in.
  const isoLowerBound = (d) => (d ? new Date(`${d}T00:00:00`).toISOString() : "");
  const isoUpperBound = (d) => (d ? new Date(`${d}T23:59:59.999`).toISOString() : "");

  const load = async () => {
    try {
      setLoading(true);
      const { questions, total: totalCount } = await getRecentQuestions({
        limit: rowsPerPage,
        skip: (page - 1) * rowsPerPage,
        userId: selectedUser,
        search,
        sortBy,
        sortDir,
        dateFrom: isoLowerBound(dateFrom),
        dateTo: isoUpperBound(dateTo),
      });
      setRows(questions);
      setTotal(totalCount);

      const ids = Array.from(new Set(questions.map((q) => q.user_id).filter((u) => u && u !== "unknown" && !userNames[u])));
      if (ids.length > 0) {
        resolveDisplayNames(ids).then((names) => setUserNames((prev) => ({ ...prev, ...names })));
      }
    } catch (error) {
      toast.error("Failed to load recent questions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, selectedUser, search, sortBy, sortDir, dateFrom, dateTo]);

  const applyDateRange = () => {
    setDateFrom(draftFrom);
    setDateTo(draftTo);
    setPage(1);
    setDateMenuOpen(false);
  };

  const clearDateRange = () => {
    setDraftFrom("");
    setDraftTo("");
    setDateFrom("");
    setDateTo("");
    setPage(1);
    setDateMenuOpen(false);
  };

  const dateRangeLabel = dateFrom || dateTo
    ? `${dateFrom || "…"} → ${dateTo || "…"}`
    : null;

  // Row content darkness follows its own column header's darkness exactly
  // (HEADER_ACTIVE while that column is the active sort, HEADER_INACTIVE
  // otherwise) -- so User/Query/Timestamp body text is never a different
  // shade than the header sitting directly above it.
  const bodyColorFor = (field) => (sortBy === field ? HEADER_ACTIVE : HEADER_INACTIVE);

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(field);
      // First click on User/Query starts ascending (A-Z); Timestamp starts
      // descending (most recent first) — matches the reference table's
      // default and each column's natural "most useful first" direction.
      setSortDir(field === "timestamp" ? "desc" : "asc");
    }
    setPage(1);
  };

  const filteredUserOptions = useMemo(() => {
    const term = userSearchTerm.trim().toLowerCase();
    return allUsers
      .map((u) => ({ ...u, label: labelForUser(u.user_id) }))
      .filter((u) => !term || u.label.toLowerCase().includes(term))
      .sort((a, b) => a.label.localeCompare(b.label));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allUsers, userSearchTerm, userNames]);

  const goToPage = (n) => {
    const clamped = Math.min(Math.max(1, n), totalPages);
    setPage(clamped);
  };

  const handlePageJumpSubmit = (e) => {
    e.preventDefault();
    const n = parseInt(pageJumpInput, 10);
    if (!Number.isNaN(n)) {
      goToPage(n);
    }
    setPageJumpInput("");
  };

  const rangeStart = total === 0 ? 0 : (page - 1) * rowsPerPage + 1;
  const rangeEnd = Math.min(page * rowsPerPage, total);

  return (
    <div className="bg-white rounded-lg shadow-md p-4 md:p-6 w-full">
      {/* Header / toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h2 className={`font-semibold text-base ${TITLE_TEXT}`}>Recent Queries</h2>

        <div className="flex flex-wrap items-center gap-2">
          {/* All Users filter */}
          <div className="relative" ref={userMenuRef}>
            <button
              type="button"
              onClick={() => setUserMenuOpen((o) => !o)}
              className={`flex items-center gap-2 text-xs ${BORDER} border rounded-md px-3 py-2 ${TOOLBAR_TEXT} bg-white hover:border-[#00AEEF]`}
            >
              {selectedUser ? labelForUser(selectedUser) : "All Users"}
              <LuChevronDown size={13} className={`transition-transform ${userMenuOpen ? "rotate-180" : ""}`} />
            </button>
            {userMenuOpen && (
              <div className={`absolute right-0 mt-1 w-56 bg-white ${BORDER} border rounded-md shadow-lg z-20 overflow-hidden`}>
                <div className={`flex items-center gap-2 px-2 py-2 ${ROW_BORDER} border-b`}>
                  <LuSearch size={13} className={MUTED_TEXT} />
                  <input
                    autoFocus
                    value={userSearchTerm}
                    onChange={(e) => setUserSearchTerm(e.target.value)}
                    placeholder="Search…"
                    className={`w-full text-xs outline-none ${TOOLBAR_TEXT} placeholder-gray-400`}
                  />
                </div>
                <div className="max-h-56 overflow-y-auto">
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedUser("");
                      setUserMenuOpen(false);
                      setPage(1);
                    }}
                    className={`w-full text-left px-3 py-2 text-xs hover:bg-slate-50 ${
                      !selectedUser ? "font-semibold text-[#00AEEF]" : TOOLBAR_TEXT
                    }`}
                  >
                    All Users
                  </button>
                  {filteredUserOptions.map((u) => (
                    <button
                      key={u.user_id}
                      type="button"
                      onClick={() => {
                        setSelectedUser(u.user_id);
                        setUserMenuOpen(false);
                        setPage(1);
                      }}
                      className={`w-full text-left px-3 py-2 text-xs hover:bg-slate-50 truncate ${
                        selectedUser === u.user_id ? "font-semibold text-[#00AEEF]" : TOOLBAR_TEXT
                      }`}
                      title={u.label}
                    >
                      {u.label}
                    </button>
                  ))}
                  {filteredUserOptions.length === 0 && (
                    <p className={`px-3 py-2 text-xs ${MUTED_TEXT}`}>No users found.</p>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Date range filter */}
          <div className="relative" ref={dateMenuRef}>
            <button
              type="button"
              onClick={() => {
                if (!dateMenuOpen) {
                  setDraftFrom(dateFrom);
                  setDraftTo(dateTo);
                }
                setDateMenuOpen((o) => !o);
              }}
              title={dateRangeLabel || "Filter by date"}
              className={`flex items-center gap-2 text-xs ${BORDER} border rounded-md px-3 py-2 bg-white hover:border-[#00AEEF] ${
                dateRangeLabel ? "text-[#00AEEF] border-[#00AEEF]" : TOOLBAR_TEXT
              }`}
            >
              <LuCalendar size={14} />
              {dateRangeLabel && <span className="whitespace-nowrap">{dateRangeLabel}</span>}
            </button>
            {dateMenuOpen && (
              <div className={`absolute right-0 mt-1 w-64 bg-white ${BORDER} border rounded-md shadow-lg z-20 p-3`}>
                <div className="flex flex-col gap-2">
                  <label className="flex flex-col gap-1">
                    <span className={`text-xs ${MUTED_TEXT}`}>From</span>
                    <input
                      type="date"
                      value={draftFrom}
                      max={draftTo || undefined}
                      onChange={(e) => setDraftFrom(e.target.value)}
                      className={`text-xs ${BORDER} border rounded-md px-2 py-1.5 ${TOOLBAR_TEXT} focus:outline-none focus:ring-1 focus:ring-[#00AEEF]`}
                    />
                  </label>
                  <label className="flex flex-col gap-1">
                    <span className={`text-xs ${MUTED_TEXT}`}>To</span>
                    <input
                      type="date"
                      value={draftTo}
                      min={draftFrom || undefined}
                      onChange={(e) => setDraftTo(e.target.value)}
                      className={`text-xs ${BORDER} border rounded-md px-2 py-1.5 ${TOOLBAR_TEXT} focus:outline-none focus:ring-1 focus:ring-[#00AEEF]`}
                    />
                  </label>
                  <div className="flex items-center justify-between mt-1">
                    <button
                      type="button"
                      onClick={clearDateRange}
                      className={`text-xs ${MUTED_TEXT} hover:text-gray-600`}
                    >
                      Clear
                    </button>
                    <button
                      type="button"
                      onClick={applyDateRange}
                      className="text-xs px-3 py-1.5 rounded-md bg-[#00AEEF] text-white font-semibold hover:bg-[#0098d1]"
                    >
                      Apply
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Search query history */}
          <div className="relative">
            <LuSearch size={13} className={`absolute left-2.5 top-1/2 -translate-y-1/2 ${MUTED_TEXT}`} />
            <input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search query history…"
              className={`text-xs ${BORDER} border rounded-md pl-7 pr-7 py-2 ${TOOLBAR_TEXT} bg-gray-50 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-[#00AEEF] w-48`}
            />
            {searchInput && (
              <button
                type="button"
                onClick={() => setSearchInput("")}
                className={`absolute right-2 top-1/2 -translate-y-1/2 ${MUTED_TEXT} hover:text-gray-600`}
              >
                <LuX size={13} />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Table — table-fixed + an explicit colgroup so User/Query/Timestamp
          each get an equal (1/3) share of the row, instead of shrinking to
          content and leaving Query to soak up whatever's left over. */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm table-fixed">
          <colgroup>
            <col className="w-1/3" />
            <col className="w-1/3" />
            <col className="w-1/3" />
          </colgroup>
          <thead>
            <tr className={`${ROW_BORDER} border-b`}>
              <th className="text-left py-2 pr-4"><SortHeader label="User" field="user" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} /></th>
              <th className="text-left py-2 pr-4"><SortHeader label="Query" field="query" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} /></th>
              <th className="text-right py-2 pr-0"><SortHeader label="Timestamp" field="timestamp" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} align="right" /></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={3} className={`py-6 text-center text-sm ${BODY_SECONDARY}`}>Loading…</td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={3} className={`py-6 text-center text-sm ${BODY_SECONDARY}`}>No questions found.</td>
              </tr>
            ) : (
              rows.map((q, i) => (
                <tr key={`${q.session_id}-${q.message_id || i}`} className={`${ROW_BORDER} border-b hover:bg-slate-50`}>
                  <td className={`py-2.5 pr-4 ${bodyColorFor("user")} truncate`} title={labelForUser(q.user_id)}>{labelForUser(q.user_id)}</td>
                  <td className={`py-2.5 pr-4 ${bodyColorFor("query")} truncate`} title={q.question}>{q.question}</td>
                  <td className={`py-2.5 pr-0 text-right ${bodyColorFor("timestamp")} truncate`}>{formatTimestamp(q.time)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Footer: rows-per-page, showing X-Y of Z, pagination + page-jump */}
      <div className={`flex flex-wrap items-center justify-between gap-3 mt-4 pt-3 ${ROW_BORDER} border-t`}>
        <div className={`flex items-center gap-3 text-xs ${BODY_SECONDARY}`}>
          <span>{total === 0 ? "No results" : `Showing ${rangeStart} - ${rangeEnd} of ${total}`}</span>
          <label className="flex items-center gap-1.5">
            Rows per Page
            <select
              value={rowsPerPage}
              onChange={(e) => {
                setRowsPerPage(Number(e.target.value));
                setPage(1);
              }}
              className={`${BORDER} border rounded-md px-1.5 py-1 text-xs bg-white ${TOOLBAR_TEXT} focus:outline-none focus:ring-1 focus:ring-[#00AEEF]`}
            >
              {ROWS_PER_PAGE_OPTIONS.map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </label>
        </div>

        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => goToPage(page - 1)}
            disabled={page <= 1}
            className={`flex items-center gap-1 text-xs px-2 py-1.5 rounded-md ${TOOLBAR_TEXT} hover:bg-slate-100 disabled:opacity-40 disabled:hover:bg-transparent`}
          >
            <LuChevronLeft size={14} /> Prev
          </button>

          {buildPageList(page, totalPages).map((p, i) =>
            p === "…" ? (
              <span key={`gap-${i}`} className={`px-1.5 text-xs ${MUTED_TEXT}`}>…</span>
            ) : (
              <button
                key={p}
                type="button"
                onClick={() => goToPage(p)}
                className={`text-xs w-7 h-7 rounded-md ${
                  p === page ? "bg-[#00AEEF] text-white font-semibold" : `${TOOLBAR_TEXT} hover:bg-slate-100`
                }`}
              >
                {p}
              </button>
            )
          )}

          <button
            type="button"
            onClick={() => goToPage(page + 1)}
            disabled={page >= totalPages}
            className={`flex items-center gap-1 text-xs px-2 py-1.5 rounded-md ${TOOLBAR_TEXT} hover:bg-slate-100 disabled:opacity-40 disabled:hover:bg-transparent`}
          >
            Next <LuChevronRight size={14} />
          </button>

          {/* Type a page number to jump straight to it. */}
          <form onSubmit={handlePageJumpSubmit} className={`flex items-center gap-1 ml-2 pl-2 ${ROW_BORDER} border-l`}>
            <label htmlFor="recent-query-page-jump" className={`text-xs ${MUTED_TEXT}`}>
              Go to
            </label>
            <input
              id="recent-query-page-jump"
              type="number"
              min={1}
              max={totalPages}
              value={pageJumpInput}
              onChange={(e) => setPageJumpInput(e.target.value)}
              placeholder={String(page)}
              className={`w-14 text-xs ${BORDER} border rounded-md px-1.5 py-1 focus:outline-none focus:ring-1 focus:ring-[#00AEEF]`}
            />
            <button
              type="submit"
              className="text-xs px-2 py-1 rounded-md border border-[#00AEEF] text-[#00AEEF] hover:bg-[#00AEEF] hover:text-white transition-colors"
            >
              Go
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default RecentQueryLogTable;
